"use client";

import type { PlainDate } from "@kedaat/engine";
import { useCallback, useEffect, useState } from "react";
import {
  DEFAULT_SETTINGS,
  loadStoredSettings,
  sanitizeSettings,
  settingsFromUrl,
  storeSettings,
  type Settings,
} from "./settings";

export interface SettingsState {
  settings: Settings;
  update: (change: (s: Settings) => Partial<Settings>) => void;
  /** False until settings have been read from the URL and this browser's storage. */
  ready: boolean;
  /** The date named in the URL, if any. */
  urlDate: PlainDate | null;
}

/**
 * The user's settings, shared by every page: the link wins, then the page's locality,
 * then this browser's storage. Changes are saved back to storage.
 */
export function useSettings(pageLocality?: number): SettingsState {
  const [settings, setSettings] = useState<Settings>(() =>
    pageLocality === undefined ? DEFAULT_SETTINGS : { ...DEFAULT_SETTINGS, locality: pageLocality },
  );
  const [ready, setReady] = useState(false);
  const [urlDate, setUrlDate] = useState<PlainDate | null>(null);

  useEffect(() => {
    const stored = loadStoredSettings();
    const fromUrl = settingsFromUrl(
      new URLSearchParams(window.location.search),
      pageLocality === undefined ? stored : { ...stored, locality: pageLocality },
    );
    setSettings(fromUrl.settings);
    setUrlDate(fromUrl.date);
    setReady(true);
  }, [pageLocality]);

  useEffect(() => {
    if (ready) storeSettings(settings);
  }, [settings, ready]);

  const update = useCallback(
    (change: (s: Settings) => Partial<Settings>) =>
      setSettings((s) => sanitizeSettings({ ...s, ...change(s) })),
    [],
  );

  return { settings, update, ready, urlDate };
}
